import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LoyaltyInfoModalCampaignCallout from 'loyalty/components/LoyaltyInfoModalCampaignCallout'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { useIntl } from 'react-intl'
import { isLuxLoyaltyEnabledInAgentHub } from 'agentHub/selectors/isLuxLoyaltyEnabledInAgentHub'
import Table from 'LuxKit/Tables/Table'
import TableHeader from 'LuxKit/Tables/TableHeader'
import TableHeaderCell from 'LuxKit/Tables/TableHeaderCell'
import TableHeaderRow from 'LuxKit/Tables/TableHeaderRow'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import { useAppSelector } from 'hooks/reduxHooks'
import type { TakeoverOptions } from 'takeovers/utils/hooks/useCurrentSiteTakeover'

interface Props {
  offer?: TakeoverOptions['offer']
}

function LoyaltyGenericInfoModal({ offer }: Props) {
  const intl = useIntl()
  const shouldHideStatusCreditsAndNights = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)
  const programDisplayName = getLoyaltyProgramDisplayName()
  const programName = getLoyaltyProgramDisplayName()

  const title = shouldHideStatusCreditsAndNights
    ? intl.formatMessage({
        id: '4EgL2DIMYJHzbwCeZEi6',
        defaultMessage: 'Earning points',
        description: 'Modal title for loyalty earning info in the loyalty program section',
      })
    : intl.formatMessage({
        id: 'pgcnX__GgVh06eG42jy4',
        defaultMessage: 'Earning points, status credits & nights',
        description: 'Modal title for loyalty earning info in the loyalty program section',
      })
  const subtitle = (() => {
    if (isLuxLoyaltyEnabledInAgentHub) {
      return shouldHideStatusCreditsAndNights
        ? intl.formatMessage(
            {
              id: '_kSXi9_vgJyL6KirxlUV',
              defaultMessage: 'As a member of {programDisplayName}, the customer can earn points on eligible bookings.',
              description: 'Modal subtitle explaining loyalty membership benefits for agent hub context',
            },
            { programDisplayName },
          )
        : intl.formatMessage(
            {
              id: 'Ya_hB88JuPjGS3jwA6gh',
              defaultMessage: 'As a member of {programDisplayName}, the customer can earn points, status credits and nights on eligible bookings.',
              description: 'Modal subtitle explaining loyalty membership benefits for agent hub context',
            },
            { programDisplayName },
          )
    }

    return shouldHideStatusCreditsAndNights
      ? intl.formatMessage(
          {
            id: 'Ixc-mM9DEz3n72_a2a2D',
            defaultMessage: 'As a member of {programDisplayName}, you can earn points on eligible bookings.',
            description: 'Modal subtitle explaining loyalty membership benefits for customer-facing context',
          },
          { programDisplayName },
        )
      : intl.formatMessage(
          {
            id: 'QGnIiGsDgt9VHvnExuwd',
            defaultMessage: 'As a member of {programDisplayName}, you can earn points, status credits and nights on eligible bookings.',
            description: 'Modal subtitle explaining loyalty membership benefits for customer-facing context',
          },
          { programDisplayName },
        )
  })()
  const findOutMoreText = isLuxLoyaltyEnabledInAgentHub ? undefined : intl.formatMessage({
    id: '8LR3TQo1rdxuuFChm9SD',
    defaultMessage: 'Find out more',
    description: 'Secondary action button to learn more about loyalty program',
  })
  const pointsSectionHeading = intl.formatMessage(
    {
      id: '3cyxGyuFj1JOIPvg1Tae',
      defaultMessage: '{programName} Points',
      description: 'Section heading for loyalty points in the loyalty program section',
    },
    { programName },
  )
  const pointsDescription = intl.formatMessage({
    id: '6-QsHZkDKoSqQyK0_31D',
    defaultMessage: 'Earn and redeem points on hotels, tours, flights, cruises and more.',
    description: 'Description of loyalty points usage in the cruise offer or booking section',
  })
  const statusCreditsSectionHeading = intl.formatMessage(
    {
      id: 'OrwG15hRPe0UuIxzmlRS',
      defaultMessage: '{programName} Status Credits & Nights',
      description: 'Section heading for status credits and nights in a modal dialog',
    },
    { programName },
  )
  const statusCreditsDescription = intl.formatMessage({
    id: 'mvFJtoTGf6t2B3uohaOg',
    defaultMessage: 'Collect status credits and nights to move up tiers and unlock rewards including room upgrades, airport transfers, lounge passes and more.',
    description: 'Description of status credits and nights benefits in a modal dialog',
  })
  const tierHeader = intl.formatMessage({
    id: 'hl1B2WxcUUIzqBem9C6T',
    defaultMessage: 'Tier',
    description: 'Table header for loyalty tier column in the loyalty program section',
  })
  const statusCreditsHeader = intl.formatMessage({
    id: 'LAtqta9k4eMZP6U0oS94',
    defaultMessage: 'Status Credits',
    description: 'Table header for status credits column in a modal dialog',
  })
  const nightsHeader = intl.formatMessage({
    id: 'VNEJr88l47MTT3w4vCWl',
    defaultMessage: 'Nights',
    description: 'Table header for the nights column in a modal dialog',
  })
  const silverTier = intl.formatMessage({
    id: 'VFPym9YLLTYv4MQYCqc1',
    defaultMessage: 'Silver',
    description: 'Display name for the Silver tier level in the loyalty program tier progression',
  })
  const goldTier = intl.formatMessage({
    id: 'pBU5hpHoVldc60Wvjz7i',
    defaultMessage: 'Gold',
    description: 'Display name for the Gold tier level in the loyalty program tier progression panel',
  })
  const platinumTier = intl.formatMessage({
    id: 'EToXLQZupJev8ZWjja6o',
    defaultMessage: 'Platinum',
    description: 'Display name for the Platinum tier level in the loyalty program tier progression',
  })
  return <Modal
    title={title}
    subtitle={subtitle}
    secondaryActionText={findOutMoreText}
    secondaryActionTo={isLuxLoyaltyEnabledInAgentHub ? undefined : `/${LOYALTY_LANDING_PAGE}`}
    secondaryActionTarget="_blank"
  >
    <Group direction="vertical" gap={16}>
      <LoyaltyInfoModalCampaignCallout offer={offer} />

      <BodyText variant="medium" weight="bold">{pointsSectionHeading}</BodyText>
      <BodyText variant="medium">
        {pointsDescription}
      </BodyText>
      {!shouldHideStatusCreditsAndNights && <>
        <BodyText variant="medium" weight="bold">{statusCreditsSectionHeading}</BodyText>
        <BodyText variant="medium">
          {statusCreditsDescription}
        </BodyText>

        <Table>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell>{tierHeader}</TableHeaderCell>
              <TableHeaderCell>{statusCreditsHeader}</TableHeaderCell>
              <TableHeaderCell>{nightsHeader}</TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            <TableBodyRow>
              <TableBodyCell>{silverTier}</TableBodyCell>
              <TableBodyCell>{intl.formatNumber(200)}</TableBodyCell>
              <TableBodyCell>{intl.formatNumber(10)}</TableBodyCell>
            </TableBodyRow>
            <TableBodyRow>
              <TableBodyCell>{goldTier}</TableBodyCell>
              <TableBodyCell>{intl.formatNumber(600)}</TableBodyCell>
              <TableBodyCell>{intl.formatNumber(20)}</TableBodyCell>
            </TableBodyRow>
            <TableBodyRow>
              <TableBodyCell>{platinumTier}</TableBodyCell>
              <TableBodyCell>{intl.formatNumber(1000)}</TableBodyCell>
              <TableBodyCell>{intl.formatNumber(30)}</TableBodyCell>
            </TableBodyRow>
          </TableBody>
        </Table>
      </>}
    </Group>
  </Modal>
}

export default LoyaltyGenericInfoModal
